import React from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";
import cx from "classnames";
import FocusLock from "react-focus-lock";
import Button from "../Button/Button";
import IconButton from "../IconButton/IconButton";
import {injectIntl} from "react-intl";
import {DIALOG_OVERLAY_ACCESSIBILITY_TEXT, KEYBOARD_ESCAPE_CODE} from "./utils";
import "./Modal.scss";

class Modal extends React.Component {
  constructor(props) {
    super(props);

    this.handleCallToAction = this.handleCallToAction.bind(this);
    this.handleClose = this.handleClose.bind(this);
    this.handleOpen = this.handleOpen.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.onClickAway = this.onClickAway.bind(this);
    this.setCloseButtonRef = this.setCloseButtonRef.bind(this);
    this.setDialogContainerRef = this.setDialogContainerRef.bind(this);
  }

  static propTypes = {
    callToAction: PropTypes.oneOfType([
      PropTypes.element,
      PropTypes.node,
      PropTypes.string
    ]),
    children: PropTypes.oneOfType([PropTypes.element, PropTypes.node]),
    className: PropTypes.oneOfType([
      PropTypes.array,
      PropTypes.number,
      PropTypes.object,
      PropTypes.string
    ]),
    ctaDisabled: PropTypes.bool,
    dialogDescriptionID: PropTypes.string.isRequired,
    dialogTitleID: PropTypes.string.isRequired,
    dismissModalButton: PropTypes.oneOfType([
      PropTypes.element,
      PropTypes.node,
      PropTypes.string
    ]),
    intl: PropTypes.object.isRequired,
    onAction: PropTypes.func,
    onClose: PropTypes.func,
    onOpen: PropTypes.func,
    role: PropTypes.oneOf(["dialog", "alertdialog"]).isRequired
  };

  static defaultProps = {
    ctaDisabled: false,
    role: "dialog"
  };

  componentWillUnmount() {
    this.handleClose();
  }

  handleCallToAction(e) {
    const {onAction} = this.props;

    onAction ? onAction(e, this.handleClose) : this.handleClose(e);
  }
  
  handleClose(e) {
    const {onClose} = this.props;

    this.toggleScrollLock(false);
    onClose && onClose(e);
  }

  handleOpen(e) {
    const {onOpen} = this.props;
    
    onOpen && onOpen(e);

    this.setState({
      isOpen: true
    }, () => {
      this.closeDialogButton.focus();
    });
    this.toggleScrollLock();
  }
  
  handleKeyDown(e) {
    return e.keyCode === KEYBOARD_ESCAPE_CODE && this.handleClose(e);
  }

  onClickAway(e) {
    if (this.dialogContainer && this.dialogContainer.contains(e.target)) {
      return;
    }
    this.handleClose(e);
  }

  setCloseButtonRef(element) {
    this.closeDialogButton = element;
  }

  setDialogContainerRef(element) {
    this.dialogContainer = element;
  }

  toggleScrollLock(enable) {
    document.querySelector('html').classList.toggle('scroll-lock', enable);
  }

  render() {
    
    const {
      isOpen,
      callToAction,
      children,
      className,
      ctaDisabled,
      dialogDescriptionID,
      dialogTitleID,
      dismissModalButton,
      intl,
      role
    } = this.props;

    const lockProps = {
      "aria-labelledby": dialogTitleID,
      "aria-describedby": dialogDescriptionID,
      "aria-modal": "true",
      onClick: this.onClickAway,
      onKeyDown: this.handleKeyDown,
      role: role,
      tabIndex: "-1"
    };

    const ModalClassNames = cx("Modal", className);
    const containerClassNames = cx('Modal__container', {
      'Modal__container--withCallToAction': callToAction
    });

    this.toggleScrollLock(isOpen);

    return (
      <>
        {isOpen &&
          ReactDOM.createPortal(
            <FocusLock
              lockProps={lockProps}
              className={ModalClassNames}
              persistentFocus={false}
              as="aside">
              <div
                className={containerClassNames}
                ref={this.setDialogContainerRef}>
                <IconButton
                  buttonRef={this.setCloseButtonRef}
                  className="Modal__dismissButton"
                  desc={intl.formatMessage(DIALOG_OVERLAY_ACCESSIBILITY_TEXT.desc)}
                  icon="close"
                  id="dialog-close"
                  onClick={this.handleClose}
                  styles={{height: "15px", width: "16px"}}
                  title={intl.formatMessage(DIALOG_OVERLAY_ACCESSIBILITY_TEXT.title)}/>
                <div className="Modal__content">
                  {children}
                </div>
                <div className={`Modal__buttonsWrapper`}>
                  {dismissModalButton && (
                    <Button className="Modal__dismissModal" color="white" onClick={this.handleClose}>
                      {dismissModalButton}
                    </Button>
                  )}
                  {callToAction && 
                  <Button disabled={ctaDisabled} className="Modal__callToAction" onClick={this.handleCallToAction}>
                    {callToAction}
                  </Button>
                  }
                </div>
              </div>
            </FocusLock>,
            document.body
          )
        }
      </>
    );
  }
}

export default injectIntl(Modal);
